/**
 * workflows/uploadWorkflow.ts
 *
 * New UPLOAD workflow using the extracted architecture:
 *   Source (local files) → uploader jobs → MetadataEditor → BL bind/update
 *
 * Replaces the legacy flow:
 *   filesSelectedForUpload → rootScenario → uploadToCreativeFolderBasic
 *     → loadBLAndFillJobs → checkSubmit → createFilesAndBLEntities
 *
 * Can be called from a listener middleware scenario with feature-flag fallback.
 */

import Notifications from 'react-notification-system-redux';
import { v1 as uuid } from 'uuid';
import { WebkitFile } from '@myua/dropzone';
import { INodeToCHNode } from '../../../../general/node';
import { _select, _take, ListenerApiLight } from '../../../listenerMiddleware';
import { loadBLContext } from '../blContextService';
import { updateWorkflow } from './updateWorkflow';
import * as uploaderActions from '../../uploader/actions';
import { getJobs } from '../../uploader/selectors';
import { JobStatus, jobIsFailed, jobIsSettled, jobIsSuccessful } from '../../uploader/model';

async function waitForWorkflowUploadGroup(lapi: ListenerApiLight, groupID: string) {
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const jobs = _select(lapi, getJobs).filter((job) => job.groupID === groupID);

    if (jobs.length !== 0 && jobs.every(jobIsSettled)) {
      return jobs;
    }

    const action = await _take(
      lapi,
      uploaderActions.jobsInserted,
      uploaderActions.jobUpdated,
      uploaderActions.groupDeleted,
      uploaderActions.cancelAllJobs,
    );

    if (action.type === uploaderActions.cancelAllJobs.type) {
      return null;
    }

    if (action.type === uploaderActions.groupDeleted.type && action.payload.groupID === groupID) {
      return null;
    }
  }
}

function completeUploaderGroup(lapi: ListenerApiLight, groupID: string) {
  const jobs = _select(lapi, getJobs).filter((job) => job.groupID === groupID);
  jobs.forEach((job) => {
    if (job.status !== JobStatus.FINISH) {
      lapi.dispatch(
        uploaderActions.jobUpdated({
          jobID: job.uuid,
          jobProps: {
            status: JobStatus.FINISH,
          },
        }),
      );
    }
  });
}

// ─── Workflow ────────────────────────────────────────────────────────────────

/**
 * Upload local files to a creative folder.
 *
 * @param lapi  Listener API for dispatch/signal
 * @param files Local files selected by the user
 * @param targetCreativeNodeId  Node ID of the creative folder
 */
export async function uploadWorkflow(
  lapi: ListenerApiLight,
  files: WebkitFile[],
  targetCreativeNodeId: number,
) {
  // 1. Validate that the target folder is a creative folder.
  const ctxResult = await loadBLContext(lapi, targetCreativeNodeId);
  lapi.signal.throwIfAborted();

  if (!ctxResult.success) {
    lapi.dispatch(
      Notifications.error({
        title: 'Error',
        message: 'Failed to load creative context',
      }),
    );
    return;
  }

  // 2. Hand files over to uploader jobs so the existing job viewer and retry logic are reused.
  const groupID = uuid();
  const groupName = files.length === 1 ? files[0].name : `Creative upload (${files.length} files)`;

  lapi.dispatch(
    uploaderActions.workflowUploadRequested({
      groupID,
      groupName,
      files,
      parentNodeID: targetCreativeNodeId,
      parentIsAssetLab: false,
    }),
  );

  const jobs = await waitForWorkflowUploadGroup(lapi, groupID);
  if (!jobs || jobs.length === 0) {
    return;
  }

  lapi.signal.throwIfAborted();

  if (
    !jobs.every(jobIsSuccessful) ||
    jobs.some(jobIsFailed) ||
    jobs.some((job) => !job.createdNode)
  ) {
    lapi.dispatch(
      Notifications.error({
        title: 'Upload error',
        message: 'Metadata editor was not opened because some files were not uploaded.',
      }),
    );
    return;
  }

  const uploadedNodes = jobs
    .map((job) => job.createdNode)
    .filter((node): node is Exclude<typeof node, null> => node !== null)
    .map(INodeToCHNode);

  const metadataApplied = await updateWorkflow(lapi, uploadedNodes, targetCreativeNodeId, {
    notify: false,
  });

  if (!metadataApplied) {
    return;
  }

  completeUploaderGroup(lapi, groupID);
  lapi.dispatch(Notifications.success({ title: 'All creatives have been created successfully' }));
}
